import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

export function Markdown({
  children,
  className = "",
}: {
  children: string;
  className?: string;
}) {
  const source = visibleMarkdownSource(children);
  return (
    <div className={`a3-markdown ${className}`.trim()}>
      {/* HTML in a message is never rendered: React shows it as the text that
          was written, so nothing a person or model typed disappears. */}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[lineBreaksFromHtml]}
        components={markdownComponents}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Made once, not on every render: a new function is a new element type, so
 * React would replace each link whenever the chat polls, losing clicks and
 * text selection on messages that haven't changed.
 */
const markdownComponents: Components = {
  a: ({ node: _node, children: content, ...props }) => (
    <a {...props} target="_blank" rel="noreferrer noopener">
      {content}
    </a>
  ),
  img: ({ node: _node, src, alt }) =>
    src ? (
      <a href={src} target="_blank" rel="noreferrer noopener">
        {alt?.trim() || "Open image"}
      </a>
    ) : null,
};

interface HtmlTreeNode {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HtmlTreeNode[];
}

/**
 * Models write `<br>` for a line break inside a table cell. Show it as the
 * line break it means; any other HTML stays visible as text.
 */
function lineBreaksFromHtml() {
  const visit = (node: HtmlTreeNode) => {
    if (!node.children) return;
    node.children = node.children.map((child) => {
      if (child.type === "raw" && /^<br\s*\/?>$/i.test(child.value?.trim() ?? "")) {
        return { type: "element", tagName: "br", properties: {}, children: [] };
      }
      visit(child);
      return child;
    });
  };
  return (tree: HtmlTreeNode) => visit(tree);
}

/**
 * CommonMark reads a bare numeric answer such as `42.` as an empty ordered
 * list item. Escape only that whole-message edge case so the exact model text
 * remains visible; ordinary ordered lists retain their Markdown semantics.
 */
export function visibleMarkdownSource(value: string): string {
  if (!/^\s{0,3}\d{1,9}[.)]\s*$/.test(value)) return value;
  return value.replace(/([.)])(\s*)$/, "\\$1$2");
}
